import { v4 as uuidv4 } from "https://jspm.dev/uuid";

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.selectElements();
    this.attachListeners();
  }

  connectedCallback() {
    if (!this.stickyAtc) return;

    window.addEventListener("scroll", this.syncStickyAtc, { passive: true });
    window.addEventListener("resize", this.syncStickyAtc);
    this.syncStickyAtc();
  }

  disconnectedCallback() {
    if (!this.stickyAtc) return;

    window.removeEventListener("scroll", this.syncStickyAtc);
    window.removeEventListener("resize", this.syncStickyAtc);
    document.removeEventListener("click", this.handleOptionInteraction, true);
    document.removeEventListener("change", this.handleOptionInteraction, true);
  }

  getInstallationSelect() {
    const selects = this.querySelectorAll("div#custom_addon_variations select");

    for (const select of selects) {
      const hasInstallOption = select.querySelector('option[data-label-name="Installation"]');
      if (hasInstallOption) return select;
    }

    return null;
  }

  hasInstallationOption() {
    return !!this.getInstallationSelect();
  }

  setInstallationSelection(mode) {
    const select = this.getInstallationSelect();
    if (!select) return;

    const options = [...select.options];
    const find = (txt) => options.find((o) => o.textContent.toLowerCase().includes(txt));

    if (mode === "install") {
      const installOption =
        find("white glove") ||
        find("installation") ||
        options[1];

      if (installOption) select.value = installOption.value;
    }

    if (mode === "no_install") {
      const noneOption =
        find("no installation") ||
        find("shipping only") ||
        options[0];

      if (noneOption) select.value = noneOption.value;
    }
  }

  getAddOns() {
    this.selects = this.querySelectorAll("div#custom_addon_variations select");
    const addOns = [];

    const embroideryFont = this.querySelector("#embroidery-font");
    const embroideryText = this.querySelector("#embroidery-text");

    this.selects.forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption?.dataset?.variantId) return;

      if (
        select.value === "None" ||
        select.value === "No Thanks" ||
        select.value === "No, Thank You"
      ) return;

      let addOnLabel =
        select.dataset.optionLabel ||
        selectedOption.dataset.labelName ||
        "";

      // Normalize felt labels so they always match downstream expectations
      if (/felt selection|cloth selection/i.test(addOnLabel)) {
        addOnLabel = "Felt Selection";
      }

      const isFeltSelection = /felt selection|cloth selection/i.test(addOnLabel);
      const isEmbroideryColor =
        /embroidery/i.test(addOnLabel) ||
        /embroidery/i.test(select.id);

      addOns.push({
        variantId: selectedOption.dataset.variantId,
        addOnLabel,
        addOnPrice: selectedOption.dataset.addonPrice,
        addOnPriceMoney: selectedOption.dataset.addonPriceMoney,
        addOnName: isFeltSelection
          ? (selectedOption.dataset.swatchColorName || selectedOption.textContent.trim())
          : select.value
      });

      if (isEmbroideryColor) {
        if (embroideryFont && embroideryFont.value && embroideryFont.value !== "None") {
          addOns.push({
            addOnLabel: "Embroidery Font",
            addOnName: embroideryFont.value
          });
        }

        if (embroideryText && embroideryText.value && embroideryText.value.trim() !== "") {
          addOns.push({
            addOnLabel: "Embroidery Text",
            addOnName: embroideryText.value.trim()
          });
        }
      }
    });

    return addOns;
  }

  buildCartItems() {
    const searchParams = new URLSearchParams(window.location.search);

    let variantId = searchParams.has("variant")
      ? searchParams.get("variant")
      : product?.variants?.[0]?.id;

    const addOns = this.getAddOns();
    const items = [];
    const lineItemProperties = { removalIds: [], _addOnsSubTotal: 0 };

    for (const addOn of addOns) {
      if (addOn.addOnLabel === "Size") continue;

      const _uniqueKey = uuidv4();

      if (+addOn.addOnPrice) {
        items.push({
          quantity: +this.quantitySelect.value,
          id: addOn.variantId,
          properties: { _uniqueKey }
        });

        lineItemProperties[addOn.addOnLabel] = addOn.addOnName;

        lineItemProperties.removalIds.push({
          id: _uniqueKey,
          addOnName: addOn.addOnLabel
        });

        lineItemProperties._addOnsSubTotal += +addOn.addOnPrice;
      } else {
        lineItemProperties[addOn.addOnLabel] = addOn.addOnName;
      }
    }

    items.unshift({
      quantity: +this.quantitySelect.value,
      id: variantId,
      properties: lineItemProperties
    });

    return items;
  }

  async addToCart(data) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json"
      }
    });

    const json = await res.json();

    this.addToCartBanner?.show?.({ addedProduct: json.items[0] });
    window.location.href = '/cart'
  }

  selectElements() {
    this.addToCartButton = this.querySelector("#add-to-cart");
    this.quantitySelect = this.querySelector("#product-quantity-select");
    this.addToCartBanner = document.querySelector("add-to-cart-banner");
    this.installationModal = document.querySelector("installation-modal");
    this.variantSelection = this.querySelector("[data-variant-selection]");
    this.stickyAtc = this.querySelector("[data-product-sticky-atc]");
    this.stickyAtcButton = this.querySelector("[data-sticky-atc-button]");
    this.stickyAtcOptions = this.querySelector("[data-sticky-atc-options]");
    this.customerSelectedOptions = new Set();
    this.currentVariant = null;
    this.requiredOptionCount = Number(this.stickyAtcButton?.dataset.requiredOptionCount || 0);
    this.requiresFeltSelection = this.stickyAtcButton?.dataset.requiresFelt === "true";
  }

  attachListeners() {
    this.addToCartButton.addEventListener("click", (e) => {
      e.preventDefault();

      const doAddToCart = () => this.addToCart({ items: this.buildCartItems() });

      if (this.hasInstallationOption()) {
        this.installationModal?.open({
          onInstall: () => {
            this.setInstallationSelection("install");
            doAddToCart();
            this.installationModal?.close();
          },

          onNoInstall: () => {
            this.setInstallationSelection("no_install");
            doAddToCart();
            this.installationModal?.close();
          }
        });
      } else {
        doAddToCart();
      }
    });

    if (!this.stickyAtc) return;

    this.syncStickyAtcButton();
    this.syncStickyAtcOptions();

    this.syncStickyAtc = () => {
      const isPastAddToCart = this.addToCartButton.getBoundingClientRect().bottom < 0;
      this.stickyAtc.classList.toggle("is-visible", isPastAddToCart);
      this.stickyAtc.setAttribute("aria-hidden", String(!isPastAddToCart));
    };

    this.stickyAtcButton.addEventListener("click", () => {
      if (!this.hasCustomerSelectedRequiredOptions()) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      if (!this.stickyAtcButton.disabled) this.addToCartButton.click();
    });

    this.variantSelection?.addEventListener("variant-change", (event) => {
      const variant = event.detail.variant;
      this.currentVariant = variant;
      this.syncStickyAtcButton();
      this.syncStickyAtcOptions(variant);
      if (!variant) return;
    });

    this.handleOptionInteraction = (event) => {
      const target = event.target;
      if (event.type === "click" && target.tagName === "SELECT") return;

      const optionControl = target.closest(
        "[data-variant-option-value-index], [data-variant-option-index], [data-option-index]"
      );
      const optionIndex =
        optionControl?.dataset.variantOptionValueIndex ??
        optionControl?.dataset.variantOptionIndex ??
        optionControl?.dataset.optionIndex;
      if (optionIndex !== undefined) {
        this.customerSelectedOptions.add(`option:${optionIndex}`);
      }

      const productOption = target.closest("[data-product-option]")?.dataset.productOption;
      if (
        target.closest("[data-felt-option-values]") ||
        target.matches(".color-swatch-option") ||
        /felt selection|cloth selection/i.test(productOption || "")
      ) {
        this.customerSelectedOptions.add("felt");
      }

      this.syncStickyAtcButton();
      this.syncStickyAtcOptions(this.currentVariant);
    };

    document.addEventListener("click", this.handleOptionInteraction, true);
    document.addEventListener("change", this.handleOptionInteraction, true);
  }

  hasCustomerSelectedRequiredOptions() {
    let selectedOptionCount = 0;
    this.customerSelectedOptions.forEach((option) => {
      if (option.startsWith("option:")) selectedOptionCount += 1;
    });

    return (
      selectedOptionCount >= this.requiredOptionCount &&
      (!this.requiresFeltSelection || this.customerSelectedOptions.has("felt"))
    );
  }

  syncStickyAtcButton() {
    if (!this.stickyAtcButton) return;

    if (!this.hasCustomerSelectedRequiredOptions()) {
      this.stickyAtcButton.textContent = this.stickyAtcButton.dataset.selectOptionsLabel;
      this.stickyAtcButton.disabled = false;
      return;
    }

    const available = this.currentVariant
      ? this.currentVariant.available
      : !this.addToCartButton.disabled;
    this.stickyAtcButton.disabled = !available;
    this.stickyAtcButton.textContent = available
      ? this.stickyAtcButton.dataset.preOrder === "true"
        ? this.stickyAtcButton.dataset.preOrderLabel
        : this.stickyAtcButton.dataset.addToCartLabel
      : this.stickyAtcButton.dataset.soldOutLabel;
  }

  syncStickyAtcOptions(variant = this.currentVariant) {
    if (!this.stickyAtcOptions) return;

    const variantOptions = variant?.options?.join(" / ") ||
      this.stickyAtcOptions.dataset.variantOptions;
    const feltOption = this.requiresFeltSelection
      ? document.querySelector(
          '.color-swatch-option[data-label-name="Felt Selection"]:checked'
        )?.dataset.swatchColorName
      : null;

    this.stickyAtcOptions.textContent = [variantOptions, feltOption]
      .filter(Boolean)
      .join(" / ");
  }
}

if (!customElements.get("product-form")) {
  customElements.define("product-form", ProductForm);
}